import { ref } from 'vue';
import { useQualifiers } from '@/composables/useQualifiers';
import { useActiveList } from '@/composables/useActiveList';
import { useOrgStore } from '@/stores/org';
import {
    defaultOperatorOf,
    operatorLabel,
    parseFilterValue,
    splitValue,
    takesValue,
    valueShapeOf
} from '@/utils/filterOperators';
import { formatDateOnly, formatDateValue } from '@/utils/dateQualifier';

/**
 * The values behind a filter: what a picker offers, and what an id reads as.
 *
 * Every surface that lets someone filter — the search bar, the "Filter by" menu,
 * the advanced query builder — asks the same two questions, and used to answer
 * them with its own copy of the code: which records can I pick, and what is the
 * name behind the id sitting in the URL. The copies drifted (one of them refetched
 * the whole employee list on every focus, the other read it from the org store),
 * and each carried its own `employees` special case — two places to forget.
 *
 * The label cache is module-level on purpose: a chip and a menu row showing the
 * same record resolve it once for the whole page.
 */
const labelCache = ref({});

// Resolved to the reader by the server when the query runs (ValueResolver) —
// never a record id, so never looked up: /employees/@me answers 500.
const ME = '@me';

export function useQualifierValues() {
    const orgStore = useOrgStore();
    const { resolve, resolveByIds } = useQualifiers();
    const { activeList } = useActiveList();

    /**
     * The records a relation filter can be given, for a partial name.
     *
     * Employees have their own endpoint and their own store cache; everything else
     * goes through the one resolve abstraction.
     *
     * @returns {Promise<Array<{id: any, label: string, subtitle: ?string}>>}
     */
    async function optionsFor(qualifier, partial = '') {
        if (!qualifier?.entity_type) {
            return [];
        }

        if (qualifier.entity_type !== 'employees') {
            // The list on screen says what it is always scoped to, so a picker
            // offers what can actually be found there — and the search bar, which
            // lives in the navbar, gets the same answer as the "Filter by" menu.
            return resolve(qualifier.entity_type, partial, activeList.value?.staticParams || {});
        }

        // No search text: the org store already holds the list and de-duplicates
        // the request across every picker on the page.
        if (!partial) {
            const employees = await orgStore.loadEmployees();

            return employees.slice(0, 10).map(toEmployeeOption);
        }

        try {
            const { data } = await axios.get(`${orgStore.api}/employees`, { params: { query: partial } });

            return (data.data || data || []).slice(0, 10).map(toEmployeeOption);
        } catch {
            return [];
        }
    }

    function toEmployeeOption(employee) {
        return { id: employee.id, label: employee.name, subtitle: null };
    }

    function cacheKey(qualifier, value) {
        return `${qualifier.entity_type || qualifier.key}=${value}`;
    }

    /** The name behind an id, if it has already been resolved on this page. */
    function cachedLabel(qualifier, value) {
        if (String(value) === ME) {
            return i18n.t('Me');
        }

        return labelCache.value[cacheKey(qualifier, value)] || null;
    }

    /** Remember a name the user just picked, so nothing is fetched to render it back. */
    function rememberLabel(qualifier, value, label) {
        if (value === '' || value === null || value === undefined || !label) {
            return;
        }

        labelCache.value = { ...labelCache.value, [cacheKey(qualifier, value)]: label };
    }

    /**
     * The name behind an id, resolving it once and remembering it.
     *
     * @returns {Promise<?string>} null when the record cannot be named.
     */
    async function labelFor(qualifier, value) {
        if (!qualifier?.entity_type || value === '' || value === null || value === undefined) {
            return null;
        }

        const known = cachedLabel(qualifier, value);
        if (known) {
            return known;
        }

        let label = null;

        if (qualifier.entity_type === 'employees') {
            label = (await orgStore.getEmployee(value))?.name || null;
        } else {
            const [match] = await resolveByIds(qualifier.entity_type, [value]);
            label = match?.label || null;
        }

        rememberLabel(qualifier, value, label);

        return label;
    }

    /**
     * How a filter's raw URL value reads to a person: an enum option by its label,
     * a date in words, a relation by name, and the operator spelled out whenever it
     * is not the one the filter's name already implies ("Compagnie contient Braun"
     * would be noise on every chip).
     */
    function describeCondition(qualifier, raw) {
        const { operator, value } = parseFilterValue(String(raw ?? ''), qualifier);

        if (!takesValue(qualifier, operator)) {
            return operatorLabel(qualifier, operator);
        }

        const text = describeValue(qualifier, operator, value);

        if (operator === defaultOperatorOf(qualifier)) {
            return text;
        }

        return `${operatorLabel(qualifier, operator)} ${text}`.trim();
    }

    /** The value alone, in words: an option's label, a name, a date. */
    function describeValue(qualifier, operator, value) {
        if (qualifier.type === 'enum' || qualifier.type === 'boolean') {
            return (qualifier.enum_values || []).find(o => String(o.value) === value)?.label ?? value;
        }

        if (qualifier.type === 'date') {
            // A relative shortcut is a value like any other, and the filter
            // declares its wording: « Trimestre en cours », not «@this_quarter».
            // Without this every date shortcut — and there are a dozen — read
            // back as its raw token on the chip that carries it.
            const shortcut = (qualifier.enum_values || []).find(option => String(option.value) === value);
            if (shortcut) {
                return shortcut.label;
            }

            // A range names both ends; showing the raw "a..b" would leave the
            // reader to parse it.
            if (value.includes('..')) {
                return splitValue(value, '..').map(formatDateOnly).join(' — ');
            }

            return operator === defaultOperatorOf(qualifier) ? formatDateValue(value) : formatDateOnly(value);
        }

        if (qualifier.type === 'relation') {
            return cachedLabel(qualifier, value) || value;
        }

        return value;
    }

    /**
     * A condition spelled out in full — "Compagnie contient « Inc » " — for the
     * places that read as a sentence rather than as a chip, where leaving the
     * usual comparison implicit would make two different queries look alike.
     */
    function spellCondition(qualifier, operator, value) {
        const comparison = operatorLabel(qualifier, operator) || '';

        if (!takesValue(qualifier, operator)) {
            return `${qualifier.label} ${comparison}`.trim();
        }

        const shape = valueShapeOf(qualifier, operator);
        const raw = String(value ?? '');

        // A list names every value it holds, and a range both of its ends —
        // "contient au moins un VIP" would hide the three other tags asked for.
        const text =
            shape === 'list'
                ? splitValue(raw)
                      .map(part => describeValue(qualifier, operator, part))
                      .join(', ')
                : shape === 'range'
                  ? splitValue(raw, '..')
                        .map(part => describeValue(qualifier, operator, part))
                        .join(' — ')
                  : describeValue(qualifier, operator, raw);

        return `${qualifier.label} ${comparison} ${text}`.replace(/\s+/g, ' ').trim();
    }

    return { optionsFor, labelFor, cachedLabel, rememberLabel, describeCondition, spellCondition, labelCache };
}
